import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";

const isValidPhone = (value) => /^(?:\+?91|0)?[6-9]\d{9}$/.test(String(value).replace(/[\s-]/g, ""));

// Step shown after email/password when the customer's mobile number still has
// to be verified: SMS an OTP, let them enter it, then sign them in.
// props: challenge { challengeToken, phoneMasked, hasPhone }, onVerified(user), onBack()
export default function OtpStep({ challenge, onVerified, onBack }) {
  const { sendOtp, verifyOtp } = useAuth();

  const [phone, setPhone] = useState("");
  const [editingPhone, setEditingPhone] = useState(!challenge.hasPhone);
  const [phoneMasked, setPhoneMasked] = useState(challenge.phoneMasked);
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const autoSent = useRef(false);

  // resend countdown
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const send = useCallback(
    async (numberOverride) => {
      setError("");
      setBusy(true);
      try {
        const result = await sendOtp(challenge.challengeToken, numberOverride);
        setPhoneMasked(result.phoneMasked);
        setSent(true);
        setEditingPhone(false);
        setCode("");
        setDevOtp(result.devOtp || "");
        setCooldown(result.resendInSeconds || 30);
      } catch (e) {
        if (e.retryAfter) {
          // Already sent recently: just move on to entering the code.
          setSent(true);
          setEditingPhone(false);
          setCooldown(e.retryAfter);
        }
        setError(e.message || "Could not send the OTP.");
      } finally {
        setBusy(false);
      }
    },
    [challenge.challengeToken, sendOtp]
  );

  // When the account already has a number, send straight away.
  useEffect(() => {
    if (challenge.hasPhone && !autoSent.current) {
      autoSent.current = true;
      send();
    }
  }, [challenge.hasPhone, send]);

  const submitPhone = (e) => {
    e.preventDefault();
    if (!isValidPhone(phone)) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    send(phone.trim());
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError("Please enter the 6-digit OTP.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const user = await verifyOtp(challenge.challengeToken, code, phone.trim() || undefined);
      onVerified(user);
    } catch (err) {
      setError(err.message || "Could not verify the OTP.");
      setBusy(false);
    }
  };

  return (
    <div className="otp-step">
      <p className="otp-intro">
        {editingPhone
          ? "Enter your mobile number. We will text you a 6-digit OTP to verify it."
          : `We sent a 6-digit OTP to ${phoneMasked || "your mobile number"}.`}
      </p>

      {error && <div className="customer-error-alert">{error}</div>}

      {editingPhone ? (
        <form className="customer-login-form" onSubmit={submitPhone}>
          <div className="form-group">
            <label htmlFor="otp-phone">Mobile number</label>
            <input
              id="otp-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="9876543210"
              autoFocus
            />
          </div>
          <button type="submit" className="customer-login-btn" disabled={busy}>
            {busy ? "Sending..." : "Send OTP"}
          </button>
        </form>
      ) : (
        <form className="customer-login-form" onSubmit={submitCode}>
          <div className="form-group">
            <label htmlFor="otp-code">Enter OTP</label>
            <input
              id="otp-code"
              className="otp-code-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="······"
              autoFocus
              disabled={!sent}
            />
          </div>

          {devOtp && <p className="otp-dev">Test mode OTP: <strong>{devOtp}</strong></p>}

          <button type="submit" className="customer-login-btn" disabled={busy || !sent || code.length !== 6}>
            {busy ? "Verifying..." : "Verify & continue"}
          </button>

          <div className="otp-links">
            <button type="button" disabled={busy || cooldown > 0} onClick={() => send(phone.trim() || undefined)}>
              {cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}
            </button>
            <button type="button" onClick={() => { setEditingPhone(true); setError(""); }}>
              Change number
            </button>
          </div>
        </form>
      )}

      <p className="customer-login-toggle">
        <button type="button" onClick={onBack}>Back to login</button>
      </p>
    </div>
  );
}
